import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getStaffLeaves,
  approveStaffLeave,
  rejectStaffLeave,
  updateStaffLeave,
} from "@/api/new-api/staff-management/leave/leave";

// Thunks

export const fetchStaffLeaves = createAsyncThunk(
  "staffLeaves/fetchAll",
  async ({ restaurant_id, date }, { rejectWithValue }) => {
    try {
      const res = await getStaffLeaves(restaurant_id, date);
      if (res.status === "success") return res.data;
      return rejectWithValue(res.message || "Failed to fetch staff leaves");
    } catch (err) {
      return rejectWithValue(err.message || "Something went wrong");
    }
  }
);

export const approveLeave = createAsyncThunk(
  "staffLeaves/approve",
  async (leave_id, { rejectWithValue }) => {
    try {
      const res = await approveStaffLeave(leave_id);
      if (res.status === "success") return leave_id;
      return rejectWithValue(res.message || "Failed to approve leave");
    } catch (err) {
      return rejectWithValue(err.message || "Something went wrong");
    }
  }
);

export const rejectLeave = createAsyncThunk(
  "staffLeaves/reject",
  async (leave_id, { rejectWithValue }) => {
    try {
      const res = await rejectStaffLeave(leave_id);
      if (res.status === "success") return leave_id;
      return rejectWithValue(res.message || "Failed to reject leave");
    } catch (err) {
      return rejectWithValue(err.message || "Something went wrong");
    }
  }
);

export const updateLeave = createAsyncThunk(
  "staffLeaves/update",
  async ({ leave_id, data }, { rejectWithValue }) => {
    try {
      const res = await updateStaffLeave(leave_id, data);
      if (res.status === "success") return res.data;
      return rejectWithValue(res.message || "Failed to update leave");
    } catch (err) {
      return rejectWithValue(err.message || "Something went wrong");
    }
  }
);

// Initial State
const initialState = {
  leaves: [],
  getLoading: false,
  approveLoading: false,
  rejectLoading: false,
  updateLoading: false,
  success: null,
  error: null,
};

// Slice
const staffLeavesSlice = createSlice({
  name: "staffLeaves",
  initialState,
  reducers: {
    clearLeaveMessages: (state) => {
      state.success = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Leaves
      .addCase(fetchStaffLeaves.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchStaffLeaves.fulfilled, (state, action) => {
        state.getLoading = false;
        state.leaves = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchStaffLeaves.rejected, (state, action) => {
        state.getLoading = false;
        state.leaves= [],
        state.error = action.payload;
      })

      // Approve Leave
      .addCase(approveLeave.pending, (state) => {
        state.approveLoading = true;
        state.error = null;
      })
      .addCase(approveLeave.fulfilled, (state, action) => {
        state.approveLoading = false;
        state.leaves = state.leaves.map((leave) =>
          leave.id === action.payload ? { ...leave, status: "approved" } : leave
        );
        state.success = "Leave approved successfully!";
        state.requestStatus = true;
      })
      .addCase(approveLeave.rejected, (state, action) => {
        state.approveLoading = false;
        state.error = action.payload;
      })

      // Reject Leave
      .addCase(rejectLeave.pending, (state) => {
        state.rejectLoading = true;
        state.error = null;
      })
      .addCase(rejectLeave.fulfilled, (state, action) => {
        state.rejectLoading = false;
        state.leaves = state.leaves.map((leave) =>
          leave.id === action.payload ? { ...leave, status: "rejected" } : leave
        );
        state.success = "Leave rejected successfully!";
        state.requestStatus = true;
      })
      .addCase(rejectLeave.rejected, (state, action) => {
        state.rejectLoading = false;
        state.error = action.payload;
      })

      // Update Leave
      .addCase(updateLeave.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateLeave.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.leaves = state.leaves.map((leave) =>
          leave.id === action.payload.id ? action.payload : leave
        );
        state.success = "Leave updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateLeave.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearLeaveMessages } = staffLeavesSlice.actions;
export default staffLeavesSlice.reducer;
